import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { makeStyles } from '@material-ui/core/styles';

import MyTable from '../../../../utils/MyTable';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Tooltip } from "@material-ui/core";

import XLSX from 'xlsx';

//Redux
import { useDispatch, useSelector } from "react-redux";
import allActions from "../../../../../actions";

import { getConsultingId, getShortMonthName } from "../../../../utils/utils";
import { unoTheme } from "../../../../../assets/styles/unoTheme";
import { doGetStatusClientsByYear } from "../../../../../controllers/ClientController";
import { ClientAPI } from "../../../../../API/client";
import { downloadBlob, s2ab } from "../../../../../controllers/ExcelController";
import { resolveMonthStatusDair } from "../statusDairUtils";
import {
    buildContactRows,
    copyEmailsToClipboard,
    groupContactsByClient,
    uniqueEmails,
} from "../../marketing/marketingContacts";

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
    },

    statusCell: {
        fontWeight: '600',
        fontSize: '10px',
        letterSpacing: '1px',
    },

    missingCell: {
        color: unoTheme.mainGrayColor,
        fontStyle: 'italic',
    },

    //Rodapé fixo: fica fora da área rolável, então a contagem continua visível com a
    //tabela em qualquer posição do scroll.
    summary: {
        flexShrink: 0,
        padding: '10px 18px',
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        background: 'white',
        borderTop: 'solid 1px #efefef',
        borderBottomLeftRadius: '10px',
        borderBottomRightRadius: '10px',
    },

    //.defaultScrollWithHeaderContent é display:flex (linha) no UnoDashboard; as abas
    //irmãs só põem a tabela dentro dele. Aqui a tabela divide o espaço com o rodapé,
    //então a direção vira coluna.
    scrollColumn: {
        flexDirection: 'column',

        //O root do MyTable tem height:100%, que num flex column com irmão estoura o
        //container e empurra o rodapé para fora da tela. flex:1 + minHeight:0 faz a
        //tabela ocupar só o que sobra e mantém o scroll dentro dela.
        '& > div:first-child': {
            flex: 1,
            minHeight: 0,
        },
    },
}));

function MarketingTab(props, ref) {

    const {
        selectedYearClosure,
        selectedAdvisor,
        statusesDair,
        monthSelected,
        selectedStatusIds,
        selectedRecipientTypes,
    } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    const currentUser = useSelector(state => state.currentUser);

    const [statusClientsArray, setStatusClientsArray] = useState(null);
    const [contactsArray, setContactsArray] = useState(null);
    const [loading, setLoading] = useState(false);

    async function loadMarketingData(year, advisor) {

        setLoading(true);
        dispatch(allActions.innerLoadActions.setInnerLoad({
            show: true,
            loadMessage: 'Carregando destinatários...'
        }));

        setStatusClientsArray(null);
        setContactsArray(null);

        const advisorToFilter = advisor && advisor.id ? advisor.id : null;
        const consultingId = getConsultingId(currentUser);

        const [responseStatus, responseContacts] = await Promise.all([
            //true no final = agrupa por client_id, e não por label_name: dois RPPS
            //homônimos são dois destinatários distintos, não um.
            doGetStatusClientsByYear(0, 'NO_PAGINATION', year, advisorToFilter,
                consultingId, '', null, null, null, null, 'alpha_asc', true),
            ClientAPI.getClientsContacts(consultingId, advisorToFilter),
        ]);

        if (responseStatus.success && responseContacts.success) {
            setStatusClientsArray(responseStatus.body || []);
            setContactsArray(responseContacts.body.rows || []);
        } else {
            setStatusClientsArray([]);
            setContactsArray([]);
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                message: 'Falha ao carregar os destinatários',
                type: 'error',
            }));
        }

        setLoading(false);
    }

    const contactsByClientId = useMemo(() => groupContactsByClient(contactsArray).byId, [contactsArray]);

    const recipientRows = useMemo(() => {

        if (!statusClientsArray || !contactsArray || !statusesDair || !statusesDair.length) return [];

        const month = parseInt(monthSelected) + 1;
        const filterStatus = selectedStatusIds && selectedStatusIds.length ? selectedStatusIds : null;
        const wantsType = (key) => !selectedRecipientTypes || selectedRecipientTypes.includes(key);

        const rows = [];

        statusClientsArray.forEach(clientRow => {

            const status = resolveMonthStatusDair(clientRow, month, statusesDair);

            //null = mês não aplicável ao cliente (antes do início ou depois do fim da carteira)
            if (!status) return;
            if (filterStatus && !filterStatus.includes(status.id)) return;

            const contact = contactsByClientId[clientRow.id];
            if (!contact) return;

            const baseRow = {
                client_id: clientRow.id,
                cliente: clientRow.label_name,
                status_id: status.id,
                status_name: status.name,
                status_short_name: status.short_name,
                status_color: status.color,
            };

            buildContactRows(baseRow, contact, wantsType).forEach(row => rows.push(row));
        });

        return rows;
    }, [statusClientsArray, contactsArray, contactsByClientId, statusesDair,
        monthSelected, selectedStatusIds, selectedRecipientTypes]);

    const emails = useMemo(() => uniqueEmails(recipientRows), [recipientRows]);

    function doExcel() {

        if (!recipientRows.length) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                message: 'Nenhum destinatário para exportar',
                type: 'warning',
            }));
            return;
        }

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: true, loadMessage: 'Gerando planilha...' }));

        const sheetName = 'Destinatários';
        const wb = XLSX.utils.book_new();
        wb.Props = { Title: sheetName, Author: 'UNO' };
        wb.SheetNames.push(sheetName);

        const ws_data = [['CLIENTE', 'SIGLA', 'STATUS DAIR', 'TIPO', 'NOME', 'E-MAIL']];

        recipientRows.forEach(row => ws_data.push([
            row.cliente,
            row.status_short_name,
            row.status_name,
            row.tipo,
            row.nome,
            row.email,
        ]));

        const ws = XLSX.utils.aoa_to_sheet(ws_data);
        ws['!cols'] = [{ wch: 34 }, { wch: 8 }, { wch: 24 }, { wch: 16 }, { wch: 34 }, { wch: 36 }];
        wb.Sheets[sheetName] = ws;

        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'binary' });
        const fileName = 'marketing_emails_'
            + getShortMonthName(parseInt(monthSelected) + 1) + '_' + selectedYearClosure;

        downloadBlob(new Blob([s2ab(wbout)], { type: 'application/octet-stream' }), fileName + '.xlsx');

        dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
    }

    function doCopyEmails(separator) {
        copyEmailsToClipboard(emails, separator, dispatch);
    }

    useImperativeHandle(ref, () => ({
        doExcel,
        doCopyEmails,
    }));

    useEffect(() => {
        loadMarketingData(selectedYearClosure, selectedAdvisor);
    }, [selectedYearClosure, selectedAdvisor]);

    //Diferente das abas irmãs, aqui o "vazio" depende dos filtros locais e não da
    //requisição, então o emptyMessage acompanha as linhas derivadas.
    useEffect(() => {

        if (loading) return;
        if (!statusClientsArray || !contactsArray) return;

        if (!recipientRows.length) {
            dispatch(allActions.innerLoadActions.setInnerLoad({
                show: true,
                emptyMessage: 'Nenhum destinatário encontrado',
            }));
        } else {
            dispatch(allActions.innerLoadActions.setInnerLoad({ show: false }));
        }

    }, [recipientRows, loading, statusClientsArray, contactsArray]);

    return (
        <div className={classes.root}>

            <div className={['defaultScrollWithHeaderContent', classes.scrollColumn].join(' ')}>

                <MyTable
                    stickyHeader
                    withBorderColumn
                    size="small">
                    <colgroup>
                        <col width="26%" />
                        <col width="8%" />
                        <col width="18%" />
                        <col width="12%" />
                        <col width="36%" />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell align="left">Cliente</TableCell>
                            <TableCell align="center">Status</TableCell>
                            <TableCell align="left">Tipo</TableCell>
                            <TableCell align="left">Nome</TableCell>
                            <TableCell align="left">E-mail</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            recipientRows.map((row, index) => (
                                <TableRow key={'marketing-row-' + row.client_id + '-' + index}>
                                    <TableCell align="left" className={'ellipsis'}>{row.cliente}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip button
                                            title={row.status_name}
                                            enterDelay={200}
                                            leaveDelay={100}
                                            placement="bottom"
                                            arrow>
                                            <div className={classes.statusCell} style={{ color: row.status_color }}>
                                                {row.status_short_name}
                                            </div>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="left"
                                        className={row.email ? null : classes.missingCell}>{row.tipo}</TableCell>
                                    <TableCell align="left" className={'ellipsis'}>{row.nome}</TableCell>
                                    <TableCell align="left" className={'ellipsis'}>{row.email}</TableCell>
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </MyTable>

                {
                    recipientRows.length ?
                        <div className={classes.summary}>
                            {recipientRows.length} destinatários{' / '}{emails.length} e-mails únicos
                        </div> : null
                }

            </div>

        </div>
    );
}

export default forwardRef(MarketingTab);
